<template>
  <div class="time" @mouseover="isTimeTooltip = true" @mouseleave="isTimeTooltip = false">
    <ui-tooltip v-if="!isPlusPlan && isTimeTooltip"
                arrowBottom
                arrowCenter
                class="time-tooltip">
      <p v-html="$t('common.timeTooltip')"></p>
      <a :href="tooltipLink" target="_blank">
        {{ $t('heatmap.cvr_exit.frequency.goLink') }}
        <i class="icon icon-link"></i>
      </a>
    </ui-tooltip>
    <ul>
      <li class="time-container">
        <span class="time-title hour">{{ $t('common.timeSelection') }}</span>
        <div class="time--wrap" :class="{disabled: isStartTimeDisabled}" ref="dropdown">
            <span class="current-time" :class="{en: $i18n.locale === 'en' && startTime === $t('common.instantlyStart')}" @click.stop.prevent="showTimeList(true, 'start')">
              {{ startTime }}
            </span>
          <i class="icon icon--drop-down" :class="{disabled: isStartTimeDisabled}"></i>
          <transition name="slide">
            <ul class="time__list" :class="{'is-show': showStartTimeListStatus, en: $i18n.locale === 'en' && startTime === $t('common.instantlyStart')}" v-show="showStartTimeListStatus">
              <li v-for="(time, index) in startTimeList" :key="index" :class="{disabled: isStartTimeDisabled}"
                  @click="onTimeClick(time, 'start')">
                {{ time }}
              </li>
            </ul>
          </transition>
        </div>
      </li>
      <li class="time-container minute">
        <span class="time-title minute"> ~ </span>
        <div class="time--wrap" :class="{disabled: isEndTimeDisabled}" ref="dropdown">
            <span class="current-time" @click.stop.prevent="showTimeList(true, 'end')">
              {{ endTime }}
            </span>
          <i class="icon icon--drop-down" :class="{disabled: isEndTimeDisabled}"></i>
          <transition name="slide">
            <ul class="time__list" :class="{'is-show': showEndTimeListStatus}" v-show="showEndTimeListStatus">
              <li v-for="(time, index) in endTimeList" :key="index"
                  @click="onTimeClick(time, 'end')">
                {{ time }}
              </li>
            </ul>
          </transition>
        </div>
      </li>
    </ul>
  </div>
</template>
<script>
import { format, isBefore, isEqual, isSameDay, parse } from 'date-fns'
import { utcToLocalTimezone } from '@/libs/utils'
import UiTooltip from '@/components/ui/UiTooltip.vue'

export default {
  name: 'TimeSelect',
  components: { UiTooltip },
  props: {
    tempDateRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      }
    },
    dateRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      }
    },
    timezoneName: {
      type: String,
      required: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    bDataState: {
      type: String,
      required: false
    },
    isPlusPlan: {
      type: Boolean,
      required: false
    },
    model: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      showStartTimeListStatus: false,
      showEndTimeListStatus: false,
      startTime: null,
      endTime: null,
      now: utcToLocalTimezone(new Date(), this.timezoneName),
      isNotPlusPlan: false,
      isTimeTooltip: false
    }
  },
  created () {
  },
  mounted () {
    const formatTime = (date) => format(date, 'HH : mm')
    const isStartDateNow = isSameDay(this.tempDateRange.startDate, this.now)
    const isEndDateNow = isSameDay(this.dateRange.endDate, this.now)
    const isStartDateSame = isSameDay(this.tempDateRange.startDate, this.dateRange.startDate)
    const isEndDateSame = isSameDay(this.tempDateRange.endDate, this.dateRange.endDate)
    const isBeforeDateSame = isSameDay(this.model.startDate, this.tempDateRange.startDate)

    const setStartTime = () => {
      if (isEqual(this.dateRange.startDate, this.model.startDate) && this.bDataState) {
        if (isStartDateNow) {
          return this.$t('common.instantlyStart')
        }
        return formatTime(this.dateRange.startDate)
      }
      if (isStartDateNow) {
        return this.$t('common.instantlyStart')
      }

      return '00 : 00'
    }

    const setEndTime = () => {
      if (isEndDateSame) {
        if (this.dateRange.endDate.getMinutes() === 0 && this.dateRange.startDate.getHours() === 0) {
          return '23 : 59'
        }
        return formatTime(this.dateRange.endDate)
      }
      return '23 : 59'
    }

    if (!this.bDataState) {
      this.startTime = setStartTime()
      this.endTime = setEndTime()
    } else if (['run', 'scheduledRun'].includes(this.bDataState)) {
      this.startTime = isBeforeDateSame ? formatTime(this.model.startDate) : setStartTime()
      this.endTime = this.dateRange.endDate.getMinutes() === 0 ? '23 : 59' : formatTime(this.dateRange.endDate)
    } else if (this.bDataState === 'error') {
      this.startTime = isStartDateSame ? formatTime(this.dateRange.startDate) : setStartTime()
      this.endTime = isEndDateSame ? formatTime(this.dateRange.endDate) : setEndTime()
    } else {
      this.startTime = setStartTime()
    }
    document.querySelector('.date_range_picker').addEventListener('click', this.handleClickOutside)
  },
  watch: {
    startTime: {
      immediate: true,
      handler (newHour, oldHour) {
        const nowHour = this.now.getMinutes() > 0 ? this.now.getHours() + 1 : this.now.getHours()
        if (newHour === this.$t('common.instantlyStart') && oldHour && newHour === oldHour) {
          this.endTime = nowHour
        }

        this.$emit('onTimeChange', this.startTime, this.endTime)
      }
    }
  },
  computed: {
    startTimeList () {
      const times = []

      if (isSameDay(this.now, this.tempDateRange.startDate)) {
        times.push(this.$t('common.instantlyStart'))
        const startHour = this.now.getMinutes() > 0 ? this.now.getHours() + 1 : this.now.getHours()
        for (let i = startHour; i < 24; i++) {
          const date = utcToLocalTimezone(new Date(), this.timezoneName)
          date.setHours(i, 0, 0, 0)
          if (date > this.now) {
            times.push(format(date, 'HH : mm'))
          }
        }
      } else {
        for (let i = 0; i < 24; i++) {
          const date = utcToLocalTimezone(new Date(), this.timezoneName)
          date.setHours(i, 0, 0, 0)
          times.push(format(date, 'HH : mm'))
        }
      }

      return times
    },
    endTimeList () {
      const times = []

      if (this.startTime === this.$t('common.instantlyStart') && isSameDay(this.now, this.tempDateRange.startDate)) {
        const startHour = this.now.getMinutes() > 0 ? this.now.getHours() + 1 : this.now.getHours()
        for (let i = startHour; i < 24; i++) {
          const date = utcToLocalTimezone(new Date(), this.timezoneName)
          date.setHours(i, 59, 0, 0)
          times.push(format(date, 'HH : mm'))
        }
      } else {
        const selectedHour = parseInt(this.startTime, 10)
        const startHour = this.now.getMinutes() > 0 ? this.now.getHours() + 1 : this.now.getHours()
        for (let i = selectedHour; i < 24; i++) {
          const date = utcToLocalTimezone(new Date(), this.timezoneName)
          date.setHours(i, 59, 0, 0)
          times.push(format(date, 'HH : mm'))
        }
      }
      return times
    },
    isStartTimeDisabled () {
      return (this.bDataState === 'run') ||
        (this.bDataState === 'scheduledRun') ||
        (this.bDataState === 'endByAnalysisDate') ||
        !this.isPlusPlan ||
        (this.bDataState === 'error' && isBefore(parse(this.model.startDate), utcToLocalTimezone(new Date(), this.timezoneName)))
    },
    isEndTimeDisabled () {
      return (this.bDataState === 'endByAnalysisDate') ||
        (this.bDataState === 'endByPvLimit') ||
        !this.isPlusPlan
    },
    tooltipLink () {
      const lang = this.$i18n.locale
      if (lang === 'ko') {
        return 'https://www.beusable.net/ko/uxheatmap/pricing'
      } else if (lang === 'ja') {
        return 'https://www.beusable.net/ja/uxheatmap/pricing'
      } else {
        return 'https://www.beusable.net/en/uxheatmap/pricing'
      }
    }
  },
  methods: {
    showTimeList (status, type) {
      if (type === 'start') {
        if (status) {
          this.showStartTimeListStatus = status
          this.showEndTimeListStatus = false
        }
      } else {
        if (status) {
          this.showEndTimeListStatus = status
          this.showStartTimeListStatus = false
        }
      }
    },
    onTimeClick (index, type) {
      if (type === 'start') {
        if (index === this.$t('common.instantlyStart')) {
          this.startTime = index
        } else {
          this.startTime = index
        }
        this.showStartTimeListStatus = false
      } else {
        if (this.startTime === this.$t('common.instantlyStart')) {
          this.endTime = index
        } else {
          this.endTime = index
        }
        this.showEndTimeListStatus = false
      }

      this.$emit('onTimeChange', this.startTime, this.endTime)
      this.showTimeList(false, type)
    },
    handleClickOutside (event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.showStartTimeListStatus = false
        this.showEndTimeListStatus = false
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style lang="scss" scoped>
.time {
  padding: 25px 0;
  position: relative;
  width: fit-content;
  margin: 0 auto;

  .time-tooltip {
    top: 15%;
    right: 56%;
    transform: translate(50%, -100%);

    &.april {
      transform: translate(74%, 14%);
      .text {
        p {
          margin-bottom: 0;
        }
      }
    }

    .text {
      p {
        text-align: left;
        margin-bottom: 10px;
      }
      a {
        display: flex;
        font-size: 13px;
        font-weight: 400;
        color: #0074FF;
        text-decoration: none;
        margin-top: 10px;
        i.icon-link {
          width: 16px;
          height: 16px;
          background: url('../../assets/img/icon_link_blue_size16.svg') no-repeat center center;
          background-size: cover;
          transform: scaleX(-1);
          margin-left: 5px;
        }
      }
    }
  }

  ul {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;

    li {
      height: 40px;
      padding: 7px 10px;
      box-sizing: border-box;
      position: relative;

      &.time-container {
        display: flex;
        align-items: center;
        gap: 20px;
        height: 50px;
        padding: 12px 0px 0px 0px;

        &.minute {
          margin-left: 5px;
          gap: 5px;
        }
      }

      .time-title {
        max-width: 86px;
        width: 100%;
        font-size: 13px;
        color: #666;
      }

      .time--wrap {
        position: relative;
        max-width: 160px;
        min-width: 120px;
        width: 100%;
        height: 38px;
        border-radius: 6px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        box-sizing: border-box;

        &.disabled {
          background-color: #f7f7f7;
          cursor: not-allowed;
          pointer-events: none;

          > span {
            color: #999;
          }
        }

        .current-time,
        .time__list li{
          display: inline-block;
          width: 100%;
          height: 100%;
          padding: 11px 20px;
          box-sizing: border-box;
          font-size: 14px;
          color: #2f2f2f;
          cursor: pointer;

          &.en {
            width: 160px;
          }
        }

        .time__list {
          display: inline-block;
          width: 120px;
          max-height: 150px;
          border-radius: 6px;
          box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
          background-color: #ffffff;
          overflow-y: scroll;
          position: absolute;
          top: -1px;
          left: -1px;
          z-index: 9;
          opacity: 0;
          transform-origin: top;
          transition: transform .2s ease, opacity .1s ease;

          &.is-show {
            opacity: 1;
          }

          li:hover {
            background-color: #ebebeb;
          }

          &.en {
            width: 160px;
          }
        }

        .icon--drop-down {
          pointer-events: none;
          position: absolute;
          top: 50%;
          right: 12px;
          transform: translateY(-50%);
        }

        .slide-enter, .slide-leave-to{
          transform: scaleY(0);
          opacity: 0;
        }
      }
    }
  }
}
</style>
